/**
 * Attendance reports at three widths, plus the two the server can hand back as a
 * spreadsheet.
 *
 * All of these are tenant-scoped, so `requestControl()`, never `request()`. On
 * `GET /control/attendance` in particular `x-company-id` is REQUIRED rather than
 * merely honoured: without it the platform-admin "every company" scope would mean
 * every tenant's people at once, so the server answers 400 instead.
 *
 * The self-scoped twin (`GET /me/attendance`) lives in `entities/me/api.ts` — it takes
 * its identity from the JWT and must not be reached through this file, which would
 * send a company header as a SCOPE where /me/* treats it as a filter.
 */
import {requestControl, requestControlRaw} from '@/shared/api';
import type {
  CompanyAttendanceReport,
  ControlDepartment,
  ControlDepartments,
  DepartmentAttendanceReport,
  PersonnelAttendanceReport,
  ScheduleInput,
  TimesheetCatalog,
  TimesheetSort,
} from './types';
import {periodParams, requestPeriod, type Period} from './period';

/** "xlsx" is the server's default; csv is offered because a phone can open it. */
export type ExportFormat = 'xlsx' | 'csv';

function query(period: Period, extra: Record<string, string> = {}): string {
  // Clamped rather than validated: by the time a request is being built the UI has
  // already had its chance to complain, and a 422 from a slider nobody watched is a
  // worse outcome than the nearest legal window.
  //
  // `requestPeriod` also cuts the period off at TODAY. The engine walks calendar days
  // and stamps `absent` on every scheduled one nobody registered on — including the days
  // that have not happened yet, which made a report on the 26th call the remaining five
  // days of the month absences. Not asking about them is the only fix that reaches the
  // counters too: `summary.absent_days` counts the same days, and the company-wide
  // report carries no `days` to correct them from.
  const params = new URLSearchParams({...periodParams(requestPeriod(period)), ...extra});
  return params.toString();
}

/**
 * The whole company for a period.
 *
 * `includeDays` is off by default and should stay off for anything that only draws the
 * per-person summary table: the server spells out the cost — "a company of two hundred
 * over a month is six thousand rows". Turn it on only for the day-level views (the
 * calendar heat map, the per-day trend) that genuinely need every row.
 *
 * `departmentIds` empty means EVERYONE — including people attached to no department at
 * all, who are precisely the ones a loop over departments loses without saying so.
 */
export function fetchCompanyAttendance(
  companyId: string,
  period: Period,
  options: {departmentIds?: string[]; includeDays?: boolean} = {},
): Promise<CompanyAttendanceReport> {
  const extra: Record<string, string> = {};
  if (options.departmentIds?.length) {
    extra.department_ids = options.departmentIds.join(',');
  }
  if (options.includeDays) {
    extra.include_days = 'true';
  }
  return requestControl<CompanyAttendanceReport>(
    `/control/attendance?${query(period, extra)}`,
    companyId,
  );
}

/**
 * One department. Always carries `days` — the server does not offer an opt-out here,
 * because a department is already a bounded set of people.
 *
 * `deptId` is the sv_control `department_id` (from GET /control/departments), NOT the
 * organization id the chips in the personnel form carry. The two id spaces look
 * identical and this route resolves only the former.
 */
export function fetchDepartmentAttendance(
  companyId: string,
  deptId: string,
  period: Period,
): Promise<DepartmentAttendanceReport> {
  return requestControl<DepartmentAttendanceReport>(
    `/control/departments/${encodeURIComponent(deptId)}/attendance?${query(period)}`,
    companyId,
  );
}

/** One person, by their personnel record. */
export function fetchPersonAttendance(
  companyId: string,
  recordId: string,
  period: Period,
): Promise<PersonnelAttendanceReport> {
  return requestControl<PersonnelAttendanceReport>(
    `/control/personnel/${encodeURIComponent(recordId)}/attendance?${query(period)}`,
    companyId,
  );
}

/**
 * The department list the attendance routes are keyed by — sv_control's projection,
 * which also carries the head-counts and the inherited schedule the organization list
 * does not.
 */
export function fetchControlDepartments(
  companyId: string,
): Promise<ControlDepartments> {
  return requestControl<ControlDepartments>('/control/departments', companyId);
}

/**
 * Задать отделу график — единственная ручка, которая его пишет.
 *
 * Она узкая намеренно: **пишется только график**. Имя, код, телеграм-чат и связь с
 * оргструктурой маршрут не трогает, поэтому его можно звать из формы, где этих полей
 * нет. Возвращать авторство отделов в sv_control ради одной настройки значило бы снова
 * завести два места, где заводят отдел; а «прочитать отдел, подставить график,
 * сохранить целиком» не годится потому, что между чтением и записью отдел могли
 * переименовать — и запись вернула бы старое имя.
 *
 * `deptId` — это sv_control `department_id` (тот же, которым ключуются отчёты явки), а
 * НЕ id из списка отделов sv_organization. Два пространства идентификаторов выглядят
 * одинаково, и этот маршрут понимает только первое; карточка отдела сводит одно с
 * другим через `organization_department_id`.
 *
 * Право — `departments/update`. Ответ — сохранённый отдел целиком, поэтому вызывающему
 * не нужно перечитывать список, чтобы показать новый график.
 *
 * Личный график сотрудника перекрывает отдельский и правится в карточке человека —
 * этот вызов до него не достаёт.
 */
export function updateDepartmentSchedule(
  companyId: string,
  deptId: string,
  input: ScheduleInput,
): Promise<ControlDepartment> {
  return requestControl<ControlDepartment>(
    `/control/departments/${encodeURIComponent(deptId)}/schedule`,
    companyId,
    {method: 'PATCH', body: JSON.stringify(input)},
  );
}

/**
 * The two server-rendered exports. They answer with the file itself, so the caller
 * gets the raw Response — `shared/lib/download` turns it into a save on web.
 *
 * There is deliberately no company-wide equivalent: the gateway exposes exports for a
 * department and for a person only. A company-wide file is assembled client-side from
 * the report already on screen (see features/view-attendance/lib/csv.ts), which has the
 * side benefit of exporting exactly the columns and filters the user is looking at.
 */
export function exportDepartmentAttendance(
  companyId: string,
  deptId: string,
  period: Period,
  format: ExportFormat = 'xlsx',
  /** Язык заголовков листа. Отчёт читает тот же, кто его просил. */
  lang?: string,
): Promise<Response> {
  return requestControlRaw(
    `/control/departments/${encodeURIComponent(deptId)}/attendance/export?${query(
      period,
      lang ? {format, lang} : {format},
    )}`,
    companyId,
  );
}

/**
 * Свод по всей компании файлом.
 *
 * До этого общий отчёт собирался у нас же в CSV из того, что лежит на экране, —
 * и это работало ровно до вопроса «дайте таблицу за август по всем»: на экране
 * страница, а спрашивают период. Сервер отдаёт три листа: сводка, люди, дни.
 */
export function exportCompanyAttendance(
  companyId: string,
  period: Period,
  departmentIds: string[] = [],
  format: ExportFormat = 'xlsx',
  /** Язык заголовков листов. Отчёт читает тот же, кто его просил. */
  lang?: string,
): Promise<Response> {
  const extra: Record<string, string> = {format};
  if (departmentIds.length > 0) {
    extra.department_ids = departmentIds.join(',');
  }
  if (lang) {
    extra.lang = lang;
  }
  return requestControlRaw(
    `/control/attendance/export?${query(period, extra)}`,
    companyId,
  );
}

export function exportPersonAttendance(
  companyId: string,
  recordId: string,
  period: Period,
  format: ExportFormat = 'xlsx',
  /** Язык заголовков листа. Отчёт читает тот же, кто его просил. */
  lang?: string,
): Promise<Response> {
  return requestControlRaw(
    `/control/personnel/${encodeURIComponent(recordId)}/attendance/export?${query(
      period,
      lang ? {format, lang} : {format},
    )}`,
    companyId,
  );
}

/* ── Табель приходов и уходов ─────────────────────────────────────────────────────── */

/**
 * Справочник полей и листов табеля — то, из чего рисуются галочки.
 *
 * **Список НЕ зашивается у нас.** Сервер отдаёт только то, на что у этого вызывающего
 * есть право: у кого нет `salaries`, тот зарплатных полей и листа ведомости в ответе не
 * увидит вовсе. Отсюда два следствия, ради которых это и сделано так: клиенту не нужно
 * знать про права, а новое поле появляется в кабинете без нашего релиза.
 *
 * `lang` переводит ЗАГОЛОВКИ галочек, и это тот же язык, которым потом подпишется
 * книга: иначе человек ставит галочку «Начислено», а в файле получает «Accrued».
 */
export function fetchTimesheetFields(
  companyId: string,
  lang?: string,
): Promise<TimesheetCatalog> {
  const qs = lang ? `?lang=${encodeURIComponent(lang)}` : '';
  return requestControl<TimesheetCatalog>(
    `/control/attendance/timesheet/fields${qs}`,
    companyId,
  );
}

/**
 * Сам табель — книга XLSX, матрицей «человек × числа месяца».
 *
 * Чем он отличается от {@link exportCompanyAttendance}: тот отдаёт три фиксированных
 * листа плоскими таблицами, здесь же состав колонок заказывает вызывающий, и потому
 * это отдельная ручка, а не флаг у прежней.
 *
 * ⚠️ **Формат только `xlsx`.** `format=csv` этот маршрут отвергает — 422 с указанием на
 * `/control/attendance/export?format=csv`, — и это не пробел, а решение: матрица с двумя
 * строками в клетке в CSV не ложится, а отдать вместо неё плоский лист под тем же именем
 * значило бы выдать не то, что просили. Поэтому CSV в кабинете едет прежней ручкой, и
 * лист выгрузки говорит об этом вслух (см. TimesheetPicker).
 *
 * ⚠️ **Молча урезанного файла не бывает.** Незнакомый ключ в `fields`/`sheets` — 422 с
 * именем ключа, а не проигнорированный параметр; зарплатные ключи без права `salaries` —
 * 403 со списком ключей, а не книга без зарплатных колонок. Поэтому ключи берутся из
 * справочника, и только оттуда.
 *
 * Строки — ВЕСЬ персонал по фильтру, включая тех, у кого за период нет ни одной
 * отметки: человек, не появившийся ни разу, — это строка сплошных «Н», и ради неё табель
 * и заводят.
 */
export function exportTimesheet(
  companyId: string,
  period: Period,
  options: {
    departmentIds?: string[];
    recordIds?: string[];
    fields?: string[];
    sheets?: string[];
    sort?: TimesheetSort;
    lang?: string;
  } = {},
): Promise<Response> {
  const extra: Record<string, string> = {format: 'xlsx'};
  if (options.departmentIds?.length) {
    // camelCase — у этого маршрута свои имена параметров, не `department_ids` прежнего
    // экспорта. Незнакомый параметр гейтвей отвергает, а не игнорирует.
    extra.departmentIds = options.departmentIds.join(',');
  }
  if (options.recordIds?.length) {
    extra.recordIds = options.recordIds.join(',');
  }
  if (options.fields?.length) {
    extra.fields = options.fields.join(',');
  }
  if (options.sheets?.length) {
    extra.sheets = options.sheets.join(',');
  }
  if (options.sort) {
    extra.sort = options.sort;
  }
  if (options.lang) {
    extra.lang = options.lang;
  }
  return requestControlRaw(
    `/control/attendance/timesheet?${query(period, extra)}`,
    companyId,
  );
}
